/**
 * Type guard for plain object records.
 * Excludes null and arrays so parsed tool args can be treated as key/value maps.
 */
export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value != null && !Array.isArray(value)

/**
 * Parses tool call arguments or tool result content when it is a JSON string,
 * returning non-string values and invalid JSON unchanged.
 */
export const parseJsonContent = (value: unknown): unknown => {
  if (typeof value !== 'string') return value

  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

/**
 * Parses tool call arguments for UI display.
 */
export const parseToolCallArgumentsForDisplay = (toolCallArguments: string): unknown =>
  parseJsonContent(toolCallArguments)

/**
 * Parses tool-call arguments as a record for visible cards.
 * No-arg tools (e.g. `delete_dashboard`) often arrive with `""` / invalid JSON —
 * treat those as `{}` so HITL cards still render instead of falling back to
 * "Action completed".
 */
export const parseToolCallArgumentsRecordForDisplay = (
  toolCallArguments: string | undefined
): Record<string, unknown> => {
  const parsed = parseToolCallArgumentsForDisplay(toolCallArguments ?? '')
  return isRecord(parsed) ? parsed : {}
}
